// @flow
import {Map} from 'immutable';
import moment from 'moment';
import qs from 'qs';
import {setUpStore, Store} from 'client/framework';
import appStore from 'client/AppStore';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {resource, type QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import teacherPracticeExamsListActions from './TeacherPracticeExamsList.actions';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import type {List} from 'immutable';

class TeacherPracticeExamsListStore extends Store {
  selectableDateRangeStore: SelectableDateRangeStore;

  constructor(name: string) {
    super(name);
    this.selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      'TeacherPracticeExamsListSelectableDateRangeStore'
    );
    const initialValidDates = Map({
      startDate: null,
      endDate: null
    });
    this.setInitialData(
      Map({
        lastValidDates: initialValidDates,
        viewLoadedAtDatetime: null,
        assignmentToDelete: null,
        deleteAssignmentPromise: null
      })
    );
    this.handle(
      teacherPracticeExamsListActions.SET_ASSIGNMENT_TO_DELETE,
      this.setProperty('assignmentToDelete')
    );
    this.handle(teacherPracticeExamsListActions.SET_LAST_VALID_DATES, this._setLastValidDates);
    this.handle(teacherPracticeExamsListActions.DELETE_ASSIGNMENT, this._deleteAssignment);
  }

  _setLastValidDates({startDate, endDate}: {startDate: ?string, endDate: ?string}) {
    this.writeData(
      'lastValidDates',
      Map({
        startDate: startDate
          ? moment(startDate)
              .startOf('day')
              .subtract(1, 'second')
              .format()
          : null,
        endDate: endDate
          ? moment(endDate)
              .add(1, 'day')
              .startOf('day')
              .format()
          : null // Includes midnight
      })
    );
  }

  _deleteAssignment() {
    const deleteAssignmentPromise = this.getAssignmentToDelete().delete();
    this.writeData('deleteAssignmentPromise', deleteAssignmentPromise);
    deleteAssignmentPromise.then(() => {
      this.getTeacherPracticeExamsQuery().invalidateInterest();
      this.writeData('deleteAssignmentPromise', null);
    });
  }

  _addStatusFiltersToQuery(query, examStatus): QueryBuilder {
    const queryParam = appStore.routerProps.location.query.examStatus;
    const examStatusParam = examStatus !== undefined ? examStatus : queryParam;
    // SCHEDULED
    if (examStatusParam === 'scheduled') {
      // Start date is after now
      return query.filter({
        start_date: {
          greater_than: 'now',
          null: false
        }
      });
      // CLOSED
    } else if (examStatusParam === 'closed') {
      // All students have submitted the assignment
      // OR the assignment is past due and late submissions are not allowed
      return query.filter({
        any_of: [
          {
            'meta.count_of_incomplete_students': 0
          },
          {
            due_date: {
              less_than: 'now'
            },
            allow_late_submissions: false
          }
        ]
      });
      // OPEN
    } else if (!examStatusParam) {
      // Not scheduled and not closed
      return query.filter({
        all_of: [
          {
            any_of: [
              {
                start_date: {
                  less_than: 'now'
                }
              },
              {
                start_date: {
                  null: true
                }
              }
            ]
          },
          {
            any_of: [
              {
                due_date: {
                  greater_than: 'now'
                }
              },
              {
                due_date: {
                  less_than: 'now'
                },
                allow_late_submissions: true
              }
            ]
          }
        ],
        'meta.count_of_incomplete_students': {
          not: 0
        }
      });
      // ALL
    } else {
      return query;
    }
  }

  // eslint-disable-next-line flowtype/require-return-type
  getAssignmentCountsByStatus() {
    const statuses = {
      open: null,
      closed: 'closed',
      all: 'all',
      scheduled: 'scheduled'
    };
    return Object.entries(statuses).reduce((acc, [statusName, statusValue]) => {
      const query = this.getTeacherPracticeExamsQuery(statusValue).pageSize(0);
      query.getResource();
      const assignmentCount = query.getResourceMetadata().getIn(['page', 'total_entries']);
      acc[statusName] = assignmentCount;
      return acc;
    }, {});
  }

  getTeacherPracticeExamsQuery(examStatus): QueryBuilder {
    const query = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .filter({
        assignment_type: 'exam',
        classrooms_v1: appStore.routerProps.params.classId
      })
      .filter(
        {
          due_date: {
            greater_than_inclusive: this.getLastValidDates().get('startDate'),
            less_than_inclusive: this.getLastValidDates().get('endDate')
          }
        },
        this.shouldFilterByDueDate()
      )
      .include('sections_v1')
      .include('students_v2')
      .customQuery({
        with_meta: 'assignment_v3'
      });
    return this._addStatusFiltersToQuery(query, examStatus);
  }

  getTeacherPracticeExams(): List<AssignmentModelV3> {
    // $FlowFixMe
    return this.getTeacherPracticeExamsQuery().getResource();
  }

  shouldFilterByDueDate(): boolean {
    return this.getLastValidDates().every((date) => Boolean(date));
  }

  getSelectableDateRangeStore = () => this.selectableDateRangeStore;

  getLastValidDates = () => this.readData('lastValidDates');

  getAssignmentToDelete = () => this.readData('assignmentToDelete');

  getDeleteAssignmentPromise = () => this.readData('deleteAssignmentPromise');
}

export default new TeacherPracticeExamsListStore('TeacherPracticeExamsListStore');
